/*
 * LogisticsLayout — the shell every logistics page renders inside.
 *
 * Owns:
 *   - `data-app="logistics"` root scope (activates logistics.css tokens)
 *   - Top nav: hub back-button + app switcher + theme toggle
 *   - Wallet chip (sticky top-right, animated balance ticker)
 *   - Secondary nav (six service tabs + Wallet)
 *   - Disruption ribbon slot (populated in P4)
 *
 * Deliberately does NOT reuse EMS/MP layouts — this is a fresh shell.
 */
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sun, Moon, LogOut } from 'lucide-react';
import {
  House, Bed, ForkKnife, Truck, AirplaneTilt, ShoppingCart, Wallet,
  ArrowsClockwise, CaretLeft,
} from '@phosphor-icons/react';

import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { LogisticsExerciseProvider, useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import logisticsApi from '../../lib/logisticsApi';
import './logistics.css';

const NAV_ITEMS = [
  { to: '/hub/logistics/home', label: 'Home', Icon: House, end: true, testid: 'ls-nav-home' },
  { to: '/hub/logistics/accommodation', label: 'Stay', Icon: Bed, testid: 'ls-nav-stay' },
  { to: '/hub/logistics/food', label: 'Food', Icon: ForkKnife, testid: 'ls-nav-food' },
  { to: '/hub/logistics/transport', label: 'Transport', Icon: Truck, testid: 'ls-nav-transport' },
  { to: '/hub/logistics/flights', label: 'Flights', Icon: AirplaneTilt, testid: 'ls-nav-flights' },
  { to: '/hub/logistics/procurement', label: 'Procurement', Icon: ShoppingCart, testid: 'ls-nav-procurement' },
  { to: '/hub/logistics/wallet', label: 'Wallet', Icon: Wallet, testid: 'ls-nav-wallet' },
];

const APP_SWITCHER = [
  { label: 'Hub', path: '/hub' },
  { label: 'EMS', path: '/dashboard' },
  { label: 'Media Portal', path: '/mp/dashboard' },
];

// Format minor-unit integer -> "1,234.56"
export const formatMinor = (m) => {
  const value = (m || 0) / 100;
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Small animated pill in the header showing available balance. */
const WalletChip = ({ balanceMinor, currencySymbol = 'Ÿ', label = 'Available' }) => {
  const shown = formatMinor(balanceMinor);
  return (
    <div className="ls-glass ls-noise flex items-center gap-1.5 sm:gap-3 pl-2 sm:pl-3 pr-2.5 sm:pr-4 py-1 sm:py-1.5 rounded-full"
      style={{ borderRadius: '999px' }}
      data-testid="ls-wallet-chip">
      <span className="ls-overline hidden sm:inline" style={{ color: 'var(--ls-muted)' }}>{label}</span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={shown}
          initial={{ y: -14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 14, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="ls-mono font-semibold text-sm"
          style={{ color: 'var(--ls-primary)' }}
        >
          {currencySymbol}{shown}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

const AppSwitcher = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className="ls-btn-ghost text-xs"
        onClick={() => setOpen(v => !v)}
        data-testid="ls-app-switcher-btn"
      >
        <ArrowsClockwise size={14} weight="bold" /> Switch app
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.14 }}
              className="ls-surface absolute right-0 mt-2 min-w-[180px] py-2 z-50"
              data-testid="ls-app-switcher-menu"
            >
              {APP_SWITCHER.map((a) => (
                <button
                  key={a.path}
                  onClick={() => { setOpen(false); navigate(a.path); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--ls-accent)] hover:text-[var(--ls-primary)] transition-colors"
                  data-testid={`ls-app-switch-${a.label.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  {a.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const LogisticsLayoutInner = ({ children, walletBalance: propBalance, currencySymbol = 'Ÿ', hideNav = false }) => {
  const { isDark, toggle } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh: refreshExercises, selected: ctxExercise, selectedId: ctxExerciseId, clear: clearExercise } = useLogisticsExercise();
  const [autoBalance, setAutoBalance] = useState(0);

  const showWorkspaceNav = !hideNav && !!ctxExerciseId;

  // If a page didn't pre-compute wallet balance, fetch it once so the chip
  // is populated on every logistics screen (Coming-soon pages, etc.).
  // Scoped to the currently selected exercise.
  useEffect(() => {
    if (propBalance !== undefined && propBalance !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const w = await logisticsApi.myWallets();
        if (cancelled) return;
        const all = [...(w.personal || []), ...(w.team || [])];
        const scoped = ctxExerciseId ? all.filter(x => x.logistics_exercise_id === ctxExerciseId) : all;
        const total = scoped.reduce((s, x) => s + (x.available || 0), 0);
        setAutoBalance(total);
      } catch { /* silent — wallet chip stays at 0 */ }
    })();
    return () => { cancelled = true; };
  }, [propBalance, ctxExerciseId]);

  const walletBalance = propBalance !== undefined && propBalance !== null ? propBalance : autoBalance;

  // Mount the data-app scope on <html> so <body> and portals also pick up tokens
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.getAttribute('data-app');
    html.setAttribute('data-app', 'logistics');
    return () => {
      if (prev) html.setAttribute('data-app', prev);
      else html.removeAttribute('data-app');
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--ls-bg)' }}>
      {/* ================================================================
          TOP BAR — thin, business-serious. Contains hub back-button on left,
          wallet chip + app switcher + theme + user on right.
      ================================================================ */}
      <header className="ls-glass sticky top-0 z-30 border-b" style={{ borderColor: 'var(--ls-border)' }}>
        <div className="max-w-[1400px] mx-auto flex items-center gap-2 sm:gap-3 px-3 sm:px-5 h-12 sm:h-14">
          <button
            onClick={() => window.location.assign('/hub')}
            className="flex items-center gap-1.5 sm:gap-2 text-xs font-medium hover:text-[var(--ls-primary)] transition-colors flex-shrink-0"
            style={{ color: 'var(--ls-muted)' }}
            data-testid="ls-back-to-hub"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Hub
          </button>
          <span className="hidden sm:inline" style={{ color: 'var(--ls-border)' }}>/</span>
          <div className="hidden sm:flex items-baseline gap-2">
            <span className="ls-heading text-sm font-bold tracking-tight" style={{ color: 'var(--ls-secondary)' }}>
              Logistics
            </span>
            <span className="ls-overline hidden md:inline">& Spend Command</span>
          </div>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
            {showWorkspaceNav && (
              <button
                onClick={() => { clearExercise(); navigate('/hub/logistics'); }}
                className="ls-btn-ghost text-xs flex items-center gap-1.5"
                data-testid="ls-back-to-exercises"
              >
                <CaretLeft size={12} weight="bold" />
                <span className="max-w-[140px] truncate">{ctxExercise?.name || 'Exercises'}</span>
              </button>
            )}
            {showWorkspaceNav && <WalletChip balanceMinor={walletBalance} currencySymbol={currencySymbol} />}
            <div className="hidden sm:block"><AppSwitcher /></div>
            <button
              onClick={toggle}
              className="ls-btn-ghost !p-2"
              data-testid="ls-theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={logout}
              className="ls-btn-ghost !p-2"
              data-testid="ls-logout"
              aria-label="Log out"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Secondary nav — tabs across the six services + wallet */}
        {showWorkspaceNav && (
          <nav className="max-w-[1400px] mx-auto flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 pb-1.5 sm:pb-2 overflow-x-auto scrollbar-hide">
            {NAV_ITEMS.map(({ to, label, Icon, end, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 text-[12px] sm:text-[13px] font-medium rounded-sm whitespace-nowrap transition-all ${
                    isActive
                      ? 'text-[var(--ls-primary)] bg-[var(--ls-accent)]'
                      : 'text-[var(--ls-muted)] hover:text-[var(--ls-secondary)]'
                  }`
                }
              >
                <Icon size={14} weight={location.pathname === to ? 'fill' : 'regular'} />
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* ================================================================
          MAIN CONTENT
      ================================================================ */}
      <main className="max-w-[1400px] mx-auto px-3 sm:px-5 py-4 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          key={location.pathname}
        >
          {children}
        </motion.div>
      </main>

      {/* Footer / build tag — reinforces business-serious tone */}
      <footer className="max-w-[1400px] mx-auto px-3 sm:px-5 py-4 sm:py-6 flex flex-wrap items-center gap-2 sm:gap-3">
        <span className="ls-overline" style={{ color: 'var(--ls-muted)' }}>
          Logistics · V15.0 · Tactical Finance
        </span>
        <span className="ls-mono text-[10px]" style={{ color: 'var(--ls-muted)' }}>
          · {user?.email}
        </span>
      </footer>

    </div>
  );
};

const LogisticsLayout = (props) => (
  <LogisticsExerciseProvider>
    <LogisticsLayoutInner {...props} />
  </LogisticsExerciseProvider>
);

export default LogisticsLayout;
